import { zodResolver } from '@hookform/resolvers/zod'
import { CircleCheckIcon } from 'lucide-react'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Sku } from '@/interfaces/catalog/Sku'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { components } from '@invo.space/shared'

import { SkuEditFormSchema, SkuEditFormValues } from './schema'

export interface SkuEditFormProps {
  sku: Sku
  onSuccess?: (sku: Sku) => void
  onCancel?: () => void
  onSubmittingChange?: (isSubmitting: boolean) => void
}

export const SkuEditForm = ({
  sku,
  onSuccess,
  onCancel,
  onSubmittingChange,
}: SkuEditFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog.products.detail.skus.edit',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const methods = useForm<SkuEditFormValues>({
    resolver: zodResolver(SkuEditFormSchema),
    defaultValues: {
      name: sku.name,
    },
  })

  const onSubmit = async (values: SkuEditFormValues) => {
    setIsSubmitting(true)
    onSubmittingChange?.(true)

    try {
      const payload: components['schemas']['SkuPatch'] = {
        name: values.name,
      }

      const { data } = await API.patch<Sku>(`/api/v2/skus/${sku.id}`, payload)
      toast.success(t('success'))
      onSuccess?.(data)
    } catch (error: unknown) {
      console.error('SKU update error:', error)

      const errorMessage = extractErrorMessage(error)
      toast.error(t('error'), {
        description: errorMessage,
      })
    } finally {
      setIsSubmitting(false)
      onSubmittingChange?.(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={methods.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('form.name.label')}</FormLabel>
              <FormControl>
                <Input placeholder={t('form.name.placeholder')} {...field} disabled={isSubmitting} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormItem>
          <FormLabel>{t('form.code.label')}</FormLabel>
          <FormControl>
            <Input value={sku.code} disabled readOnly />
          </FormControl>
        </FormItem>

        <div className="flex gap-2 justify-end">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              {t('form.cancel')}
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            <CircleCheckIcon className="size-4 mr-2" />
            {t('form.submit')}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
